import React, {useDebugValue} from 'react';

import classNames from 'classnames';

import {useSearchFormContext} from '../../../hooks';

import {
    undef,
    hasEmptyDate,
    hasFilledDates,
    hasPartialDates
} from '../../../utils';

import {SearchIcon} from '../../SearchIcon';

const componentClassNamespace = 'search-form';
const componentBaseClassName = `${componentClassNamespace}__field`;
const componentClassName = `${componentClassNamespace}__dates-field`;

// eslint-disable-next-line complexity
function useComponent() {
    const {
        system: {
            options: {
                locale
            }
        },
        media: {
            settings: {
                isMobile
            }
        },
        form: {
            options: {
                autoProgress,
                showIcons
            }
        },
        dates: {
            actions: {
                setReadOnly,
                setShowContinueButton,
                setIsOpen: setDatesIsOpen,
                setCurrentValuesDefault,
                setCurrentValuesLatest,
                setLatestValues
            },
            events: {
                onCancel,
                onClose,
                onDateRangeChange,
                onDateRangeClear,
                onDisabledDayClick,
                onFocus,
                onOpen,
                onSelectionModeChange,
                onSubmit
            },
            options: {
                actionLocation,
                alertTemplateComponent,
                alertTemplateData,
                centered,
                consolidateInputs,
                dateFormatOptions,
                dayData,
                dayTemplateComponent,
                disableTextInput,
                displayYearFirst,
                enableSameDayDateRange,
                fadeOutDuration,
                id,
                inputIcon1Name,
                inputIcon2Name,
                inputName1,
                inputName2,
                maxDate,
                minDate,
                parentNode,
                placement,
                positionIconRight,
                scrollNode,
                showArrow,
                showInputIcon1,
                showInputIcon2,
                splitInputs,
                wdioPrefix
            },
            settings: {
                isOpen,
                readOnly,
                showContinueButton
            },
            translations: {
                startDateLabel,
                endDateLabel,
                directionsMessage,
                modalTitleStartDateMessage,
                modalTitleEndDateMessage,
                clearButtonLabel,
                closeButtonLabel,
                continueButtonLabel,
                calendarYearLabel,
                calendarMonthLabels,
                calendarShortMonthLabels,
                calendarDayLabels,
                calendarShortDayLabels,
                prevMonthButtonLabel,
                nextMonthButtonLabel,
                dateUnavailableMessage,
                startDateInvalidMessage,
                startDateSelectedMessage,
                endDateInvalidMessage,
                endDateSelectedMessage
            },
            latestValues: {
                startDate,
                endDate
            }
        },
        guests: {
            actions: {
                setIsOpen: setGuestsIsOpen
            }
        }
    } = useSearchFormContext();

    const className = classNames(componentBaseClassName, componentClassName);

    const inputIcon1 = (showInputIcon1 || showIcons && showInputIcon1 === undef) && (
        <SearchIcon name={inputIcon1Name}/>
    ) || null;

    const inputIcon2 = (showInputIcon2 || showIcons && showInputIcon2 === undef) && (
        <SearchIcon name={inputIcon2Name}/>
    ) || null;

    const modalTitle = (
        hasEmptyDate(startDate) && modalTitleStartDateMessage ||
        hasEmptyDate(endDate) && modalTitleEndDateMessage
    ) || null;

    const calendarLabels = {
        yearLabel: calendarYearLabel,
        monthNames: calendarMonthLabels,
        shortMonthNames: calendarShortMonthLabels,
        dayNames: calendarDayLabels,
        shortDayNames: calendarShortDayLabels
    };

    const i18n = {
        ariaLabelPrevButton: prevMonthButtonLabel,
        ariaLabelNextButton: nextMonthButtonLabel,
        ariaLabelCheckinSelected: startDateSelectedMessage,
        ariaLabelCheckoutSelected: endDateSelectedMessage,
        ariaLabelNotValidCheckin: startDateInvalidMessage,
        ariaLabelNotValidCheckout: endDateInvalidMessage,
        ariaLabelUnavailable: dateUnavailableMessage
    };

    function handleCancel(...args) {
        if (!hasPartialDates(startDate, endDate)) {
            setCurrentValuesLatest();
        } else {
            setCurrentValuesDefault();
        }

        if (autoProgress && isMobile) {
            setDatesIsOpen(false);
            setGuestsIsOpen(false);
        }

        onCancel(...args);
    }

    function handleClose(...args) {
        setReadOnly(false);

        if (autoProgress && isMobile && hasFilledDates(startDate, endDate)) {
            setGuestsIsOpen(true);
        }

        onClose(...args);
    }

    function handleDateRangeChange(...args) {
        const [
            newStartDate,
            newEndDate
        ] = args;

        const changedValues = {
            startDate: newStartDate,
            endDate: newEndDate
        };

        setLatestValues(changedValues);

        setShowContinueButton(hasFilledDates(newStartDate, newEndDate));

        onDateRangeChange(...args);
    }

    function handleDateRangeClear(...args) {
        onDateRangeClear(...args);
    }

    /* istanbul ignore next */
    function handleDisabledDayClick(...args) {
        onDisabledDayClick(...args);
    }

    function handleFocus(...args) {
        onFocus(...args);
    }

    function handleOpen(...args) {
        setReadOnly(true);

        setGuestsIsOpen(false);

        setShowContinueButton(hasFilledDates(startDate, endDate));

        onOpen(...args);
    }

    function handleSelectionModeChange(...args) {
        onSelectionModeChange(...args);
    }

    function handleSubmit(...args) {
        setCurrentValuesLatest();

        if (autoProgress && !isMobile && hasFilledDates(startDate, endDate)) {
            setTimeout(function delayedGuestsOpen() {
                setGuestsIsOpen(true);
            }, 300);
        }

        onSubmit(...args);
    }

    const result = {
        className,
        actionLocation,
        alertTemplateComponent,
        alertTemplateData,
        calendarLabels,
        centered,
        clearButtonLabel,
        closeButtonLabel,
        consolidateInputs,
        continueButtonLabel,
        dateFormatOptions,
        dayData,
        dayTemplateComponent,
        directionsLabel: directionsMessage,
        disableTextInput,
        displayYearFirst,
        enableSameDayDateRange,
        endDate,
        fadeOutDuration,
        i18n,
        id,
        inputIcon1,
        inputIcon2,
        inputLabel1: startDateLabel,
        inputLabel2: endDateLabel,
        inputName1,
        inputName2,
        isMobile,
        isOpen,
        locale,
        maxDate,
        minDate,
        modalTitle,
        onCancel: handleCancel,
        onClose: handleClose,
        onDateRangeChange: handleDateRangeChange,
        onDateRangeClear: handleDateRangeClear,
        onDisabledDayClick: handleDisabledDayClick,
        onFocus: handleFocus,
        onOpen: handleOpen,
        onSelectionModeChange: handleSelectionModeChange,
        onSubmit: handleSubmit,
        parentNode,
        placement,
        positionIconRight,
        readOnly,
        scrollNode,
        showArrow,
        showContinueButton,
        splitInputs,
        startDate,
        wdioPrefix
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
